import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import axios from 'axios';
import './App.css';
import './pages/seo-pages.css';
import { translations } from './translations';
import TravelPage from './pages/TravelPage';
import RealEstatePage from './pages/RealEstatePage';
import AdminPage from './pages/AdminPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogCategoryPage from './pages/BlogCategoryPage';
import FAQPage from './pages/FAQPage';
import ServicePage from './pages/ServicePage';
import DestinationPage from './pages/DestinationPage';
import ReviewsPage from './pages/ReviewsPage';
import ContactPage from './pages/ContactPage';
import PrivateIstanbulToursPage from './pages/PrivateIstanbulToursPage';
import IstanbulOldCityTourPage from './pages/IstanbulOldCityTourPage';
import IstanbulToursFAQPage from './pages/IstanbulToursFAQPage';
import AuthorProfilePage from './pages/AuthorProfilePage';
import LayoverToursPage from './pages/LayoverToursPage';
import LayoverChildPage from './pages/LayoverChildPage';
import TipsBuyingRealEstateIstanbulPage from './pages/TipsBuyingRealEstateIstanbulPage';
import GalataportShoreExcursionsPage from './pages/GalataportShoreExcursionsPage';
import BlogPostMumhanePage from './pages/BlogPostMumhanePage';
import ServicesPage from './pages/ServicesPage';
import FatihOldCityPage from './pages/FatihOldCityPage';

import { ChatBot } from './components/ChatBot';
import { CHAT_LABELS } from './components/chatLabels';
import { istanbulOpenNow } from './lib/widgetAnalytics';
import { CookieConsent } from './components/CookieConsent';
import { Header } from './components/Header';
import { ReviewsShowcase } from './components/ReviewComponents';
import { StickyNav } from './components/StickyNav';
import { HeroSection } from './components/HeroSection';
import { ExchangeRatesWidget } from './components/ExchangeRatesWidget';
import { WeatherWidget } from './components/WeatherWidget';
import { StatsSection } from './components/StatsSection';
import { FAQSection, getHomeFaqs } from './components/FAQSection';
import { DiningCards } from './components/DiningCards';
import { useRestaurants } from './lib/useRestaurants';
import { akinPersonSchema } from './components/AuthorComponents';
import { isArPath, hasArVersion, toArPath, toEnPath, localizePath } from './i18nRoutes';

function LanguageSync({ language, setLanguage }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (isArPath(location.pathname)) {
      if (language !== 'ar') setLanguage('ar');
    } else if (language === 'ar' && hasArVersion(location.pathname)) {
      navigate(toArPath(location.pathname) + location.search, { replace: true });
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
import { Footer } from './components/Footer';
import { SEOHead } from './components/SEOHead';
import { NewsletterSignup } from './components/NewsletterSignup';
import { CallbackForm } from './components/CallbackForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function HomePage({ language, setLanguage }) {
  const t = translations[language];
  const { restaurants } = useRestaurants();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sectionBgIndex, setSectionBgIndex] = useState(0);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [exchangeRatesError, setExchangeRatesError] = useState(null);
  const [exchangeRatesLoading, setExchangeRatesLoading] = useState(true);
  const [weather, setWeather] = useState(null);
  const [weatherError, setWeatherError] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const heroImages = [
    'https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/lo85zs2q_9576888711_a5bca2c439_b.jpg',
    'https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/epon1kww_3de7200d7e72bb958442b71d70e22ccc.jpg',
    'https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/d378u7pv_723_istiklal-caddesi-gezilecek-yerler.webp',
    'https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/q0t2modb_2280294.jpg',
    'https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/f3pigto7_ade15acf358fde3b7b818c61bb97fb4213950852.jpg',
    'https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/s8wcu2yv_IMG_8618.webp',
    'https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/amghrid5_istanbul_halic_view_panorama.jpg',
    'https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/ae61n6wd_springvandet-midt-i-parken.jpg'
  ];
  const sectionBgImages = [
    'https://customer-assets.emergentagent.com/job_6219a777-9ebb-4000-a873-c607f504c328/artifacts/wz19565z_9c.jpg',
    'https://customer-assets.emergentagent.com/job_6219a777-9ebb-4000-a873-c607f504c328/artifacts/wwitgywe_40.jpg',
    'https://customer-assets.emergentagent.com/job_6219a777-9ebb-4000-a873-c607f504c328/artifacts/ukm0anx7_74.jpg',
    'https://customer-assets.emergentagent.com/job_6219a777-9ebb-4000-a873-c607f504c328/artifacts/8elisbo1_caption%289%29.jpg',
    'https://customer-assets.emergentagent.com/job_6219a777-9ebb-4000-a873-c607f504c328/artifacts/iz9x4wud_topkap%C4%B1%20saray%C4%B1.png'
  ];

  const [email, setEmail] = useState('');
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [inquiryForm, setInquiryForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Direct API fallback functions for when backend is unreachable
  const fetchRatesDirect = () => {
    return axios.get('https://open.er-api.com/v6/latest/USD').then(res => {
      const rates = res.data.rates || {};
      const usdTry = rates.TRY || 0;
      return {
        rates: [
          { pair: 'USD/TRY', buy: +(usdTry * 0.998).toFixed(4), sell: +(usdTry * 1.002).toFixed(4) },
          { pair: 'EUR/TRY', buy: +(usdTry / rates.EUR * 0.998).toFixed(4), sell: +(usdTry / rates.EUR * 1.002).toFixed(4) },
          { pair: 'GBP/TRY', buy: +(usdTry / rates.GBP * 0.998).toFixed(4), sell: +(usdTry / rates.GBP * 1.002).toFixed(4) },
          { pair: 'CHF/TRY', buy: +(usdTry / rates.CHF * 0.998).toFixed(4), sell: +(usdTry / rates.CHF * 1.002).toFixed(4) },
          { pair: 'SAR/TRY', buy: +(usdTry / rates.SAR * 0.998).toFixed(4), sell: +(usdTry / rates.SAR * 1.002).toFixed(4) },
          { pair: 'AED/TRY', buy: +(usdTry / rates.AED * 0.998).toFixed(4), sell: +(usdTry / rates.AED * 1.002).toFixed(4) },
          { pair: 'RUB/TRY', buy: +(usdTry / rates.RUB * 0.998).toFixed(4), sell: +(usdTry / rates.RUB * 1.002).toFixed(4) },
        ],
        source: 'hakandoviz.com', updated: res.data.time_last_update_utc || '', success: true
      };
    });
  };
  const wmoIcons = { 0:'☀️',1:'🌤️',2:'⛅',3:'☁️',45:'🌫️',48:'🌫️',51:'🌦️',53:'🌧️',55:'🌧️',61:'🌧️',63:'🌧️',65:'🌧️',71:'🌨️',73:'🌨️',75:'❄️',77:'❄️',80:'🌦️',81:'🌧️',82:'🌧️',85:'🌨️',86:'🌨️',95:'⛈️',96:'⛈️',99:'⛈️' };
  const wmoDescs = { 0:'Clear sky',1:'Mainly clear',2:'Partly cloudy',3:'Overcast',45:'Foggy',48:'Rime fog',51:'Light drizzle',53:'Moderate drizzle',55:'Dense drizzle',61:'Slight rain',63:'Moderate rain',65:'Heavy rain',71:'Slight snow',73:'Moderate snow',75:'Heavy snow',80:'Slight showers',81:'Moderate showers',82:'Violent showers',95:'Thunderstorm' };
  const fetchWeatherDirect = () => {
    return axios.get('https://api.open-meteo.com/v1/forecast', { params: {
      latitude: 41.0082, longitude: 28.9784,
      current: 'temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m',
      daily: 'temperature_2m_max,temperature_2m_min,weather_code',
      timezone: 'Europe/Istanbul', forecast_days: 5
    }}).then(res => {
      const d = res.data, cur = d.current || {}, daily = d.daily || {};
      const code = cur.weather_code || 0;
      const forecast = (daily.time || []).map((t, i) => {
        const fc = (daily.weather_code || [])[i] || 0;
        return { date: t, max: daily.temperature_2m_max[i], min: daily.temperature_2m_min[i], code: fc, desc: wmoDescs[fc] || 'Unknown', icon: wmoIcons[fc] || '☁️' };
      });
      return { current: { temp: cur.temperature_2m, feels_like: cur.apparent_temperature, humidity: cur.relative_humidity_2m, wind_speed: cur.wind_speed_10m, code, desc: wmoDescs[code] || 'Unknown', icon: wmoIcons[code] || '☁️' }, forecast, success: true };
    });
  };
  const fetchRates = () => {
    setExchangeRatesLoading(true);
    setExchangeRatesError(null);
    axios.get(`${API}/exchange-rates`)
      .then(res => { setExchangeRates(res.data); setExchangeRatesLoading(false); })
      .catch(() => {
        fetchRatesDirect()
          .then(data => { setExchangeRates(data); setExchangeRatesLoading(false); })
          .catch(err => { setExchangeRatesError(err.message || 'Failed to load'); setExchangeRatesLoading(false); });
      });
  };
  const fetchWeather = () => {
    setWeatherLoading(true);
    setWeatherError(null);
    axios.get(`${API}/weather`)
      .then(res => { setWeather(res.data); setWeatherLoading(false); })
      .catch(() => {
        fetchWeatherDirect()
          .then(data => { setWeather(data); setWeatherLoading(false); })
          .catch(err => { setWeatherError(err.message || 'Failed to load'); setWeatherLoading(false); });
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchRatesStable = useCallback(() => fetchRates(), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchWeatherStable = useCallback(() => fetchWeather(), []);

  useEffect(() => {
    const slideInterval = setInterval(() => { setCurrentSlide((prev) => (prev + 1) % heroImages.length); }, 5000);
    const sectionBgInterval = setInterval(() => { setSectionBgIndex((prev) => (prev + 1) % 5); }, 8000);
    fetchRatesStable();
    fetchWeatherStable();
    const ratesInterval = setInterval(fetchRatesStable, 300000);
    const weatherInterval = setInterval(fetchWeatherStable, 900000);

    // Scroll reveal observer
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!prefersReduced) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); observer.unobserve(e.target); } });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      document.querySelectorAll('.scroll-reveal').forEach(el => observer.observe(el));
      return () => { clearInterval(slideInterval); clearInterval(sectionBgInterval); clearInterval(ratesInterval); clearInterval(weatherInterval); observer.disconnect(); };
    }
    return () => { clearInterval(slideInterval); clearInterval(sectionBgInterval); clearInterval(ratesInterval); clearInterval(weatherInterval); };
  }, [fetchRatesStable, fetchWeatherStable]); // heroImages.length is a const

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      setMessage({ text: t.newsletter.success || 'Successfully subscribed to newsletter!', type: 'success' });
      setEmail('');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 422) {
        setMessage({ text: t.newsletter.invalidEmail || 'Please enter a valid email address.', type: 'error' });
      } else {
        setMessage({ text: t.newsletter.error || 'Error subscribing. Please try again.', type: 'error' });
      }
    } finally { setLoading(false); setTimeout(() => setMessage({ text: '', type: '' }), 4000); }
  };

  const openInquiryModal = (serviceType, serviceName) => {
    setSelectedService(serviceType);
    setInquiryForm({ ...inquiryForm, message: `${t.inquiry.interestedIn} ${serviceName}` });
    setShowInquiryModal(true);
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...inquiryForm, service_type: selectedService });
      setMessage({ text: t.inquiry.inquirySuccess, type: 'success' });
      setInquiryForm({ name: '', email: '', phone: '', message: '' });
      setShowInquiryModal(false);
    } catch { setMessage({ text: t.inquiry.inquiryError, type: 'error' }); }
    finally { setLoading(false); setTimeout(() => setMessage({ text: '', type: '' }), 3000); }
  };


  const goturkiyeBlogLinks = [
    { image: 'https://customer-assets.emergentagent.com/job_47464d9f-cf49-4c35-99f5-cacc84e31d7d/artifacts/ukqigkx0_i.webp', link: 'https://digitalnomads.goturkiye.com/application-requirements-for-digital-nomad-visa-and-short-term-residence', isNomad: true },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/10-ways-to-say-hello-goodbye-in-the-most-hospitable-country-in-the-world.webp', link: 'https://goturkiye.com/blog/10-ways-to-say-hello-goodbye-in-the-most-hospitable-country-in-the-world' },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/10-Best-Souvenirs-to-Buy-from-Istanbul.webp', link: 'https://goturkiye.com/blog/10-best-souvenirs-to-buy-from-istanbul' },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/10%20Iconic%20Street%20Foods%20in%20Turkiye.webp', link: 'https://goturkiye.com/blog/10-iconic-street-foods-in-turkiye' },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/10-most-popular-spices-in-turkish-cuisine.webp', link: 'https://goturkiye.com/blog/10-most-popular-spices-in-turkish-cuisine' },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/Istanbul%20UEFA%20Champions%20League%20Final.webp', link: 'https://goturkiye.com/blog/10-things-to-do-in-istanbul-before-the-uefa-champions-league-final' },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/10-Turkish-Desserts-You-Need-to-Try.webp', link: 'https://goturkiye.com/blog/10-turkish-desserts-you-need-to-try' },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/10%20Turkish%20Souvenirs%20You%20Can%20Get%20from%20Local%20Crafts%20Shops%20in%20Turkiye.webp', link: 'https://goturkiye.com/blog/10-turkish-souvenirs-you-can-get-from-local-crafts-shops-in-turkiye' },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/16%20Foods%20You%20Have%20to%20Try%20on%20Your%20Trip%20to%20Turkiye.webp', link: 'https://goturkiye.com/blog/16-foods-you-have-to-try-on-your-trip-to-turkiye' },
    { image: 'https://storage.googleapis.com/goturkiyev2-storage/Root/goturkiye/13%20Things%20You%E2%80%99ll%20Only%20Understand%20If%20You%E2%80%99ve%20Been%20to%20Turkiye.webp', link: 'https://goturkiye.com/blog/13-things-youll-only-understand-if-youve-been-to-turkiye' }
  ];

  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TravelAgency",
        "@id": "https://viaturkey.net/#travel-agency",
        "name": "Viaturkey Investments & Tourism Co.",
        "description": "Istanbul-based travel consultancy specializing in customized travel planning, real estate investment consulting, and Turkish citizenship services. Tour bookings facilitated through TÜRSAB-licensed partner agencies.",
        "url": "https://viaturkey.net",
        "logo": "https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/py4ra2u0_channels4_profile.jpg",
        "image": "https://customer-assets.emergentagent.com/job_tourism-hub-14/artifacts/py4ra2u0_channels4_profile.jpg",
        "telephone": "+905075337545",
        "email": "akin@viaturkey.net",
        "founder": { "@id": "https://viaturkey.net/about/akin-guzel/#person" },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Yedi Emirler Sk. No:18/9",
          "addressLocality": "Fatih",
          "addressRegion": "Istanbul",
          "postalCode": "34080",
          "addressCountry": "TR"
        },
        "geo": { "@type": "GeoCoordinates", "latitude": "41.0082", "longitude": "28.9784" },
        "areaServed": { "@type": "City", "name": "Istanbul" },
        "availableLanguage": ["English", "Turkish", "Arabic", "Russian"],
        "priceRange": "$$",
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "08:00",
          "closes": "22:00"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+905075337545",
          "contactType": "customer service",
          "availableLanguage": ["English", "Arabic", "Russian", "Turkish"],
          "url": "https://viaturkey.net/contact"
        },
        "sameAs": [
          "https://www.tripadvisor.com/Attraction_Review-g293974-d25289505-Reviews-Viaturkey_Tours-Istanbul.html",
          "https://www.youtube.com/@akinguzel1490",
          "https://www.instagram.com/via_turkey",
          "https://www.tiktok.com/@akingzel",
          "https://x.com/akngzl3",
          "https://wa.me/905075337545"
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Istanbul Travel & Investment Services",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "TouristTrip", "name": "Istanbul Old City Daily Tour", "description": "Private guided tour of Hagia Sophia, Blue Mosque, Topkapi Palace, Grand Bazaar. 5-8 hours." } },
            { "@type": "Offer", "itemOffered": { "@type": "TouristTrip", "name": "Istanbul Shore Excursion", "description": "Private cruise ship shore excursion covering Istanbul's top landmarks. 5-8 hours." } },
            { "@type": "Offer", "itemOffered": { "@type": "TouristTrip", "name": "Istanbul Food Tour", "description": "Guided culinary tour through Istanbul's best street food and restaurants." } },
            { "@type": "Offer", "itemOffered": { "@type": "TouristTrip", "name": "Istanbul Airport Layover Tour", "description": "Make the most of your layover with a guided Istanbul tour. 5-8 hours." } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Real Estate Investment Consulting", "description": "Expert consultancy for luxury property investments in Istanbul, Turkey.", "url": "https://viaturkey.net/real-estate" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Turkish Citizenship by Investment Assistance", "description": "End-to-end guidance for the Turkish citizenship by investment program.", "url": "https://viaturkey.net/tours/turkish-citizenship-investment" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Turkish Residence Permit Support", "description": "Assistance with Turkish residence permit applications for property buyers and long-stay visitors." } }
          ]
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://viaturkey.net/#website",
        "url": "https://viaturkey.net",
        "name": "Viaturkey",
        "description": "Istanbul tours, real estate investment, and Turkish citizenship consultancy",
        "publisher": { "@id": "https://viaturkey.net/#travel-agency" },
        "inLanguage": ["en", "ru", "ar", "tr"],
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://viaturkey.net/?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      akinPersonSchema,
      {
        "@type": "FAQPage",
        "@id": "https://viaturkey.net/#faq",
        "mainEntity": getHomeFaqs(t, restaurants, language).map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      }
    ]
  };

  return (
    <div className="app">
      <SEOHead
        title={language === 'ar' ? (t.seo?.homeTitle || 'Istanbul Tours & Real Estate | Viaturkey') : 'Istanbul Tours & Real Estate | Viaturkey'}
        description={language === 'ar' ? (t.seo?.homeDesc || '') : "Istanbul's trusted travel consultancy. Plan private guided tours, invest in luxury Istanbul real estate, and get expert help with Turkish citizenship."}
        canonical="https://viaturkey.net/"
        schema={homeSchema}
      />
      <HeroSection t={t} heroImages={heroImages} currentSlide={currentSlide} setCurrentSlide={setCurrentSlide} language={language} />

      {/* Compliance Disclaimer */}
      <div className="homepage-disclaimer" data-testid="homepage-disclaimer">
        <div className="container">
          <p><em>{language === 'ar' ? 'فياتركي شركة استشارات سياحية وعقارية. تُنظَّم حجوزات الجولات عبر وكالات شريكة مرخّصة من TÜRSAB، بما يضمن الامتثال الكامل لأنظمة السياحة التركية.' : 'Viaturkey is a travel & real estate consultancy. Tour bookings are facilitated through TÜRSAB-licensed partner agencies, ensuring full compliance with Turkish tourism regulations.'}</em></p>
        </div>
      </div>

      {/* Featured New Project Banner */}
      <section className="new-project-banner scroll-reveal" data-testid="new-project-banner" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="new-project-bg"></div>
        <div className="new-project-overlay"></div>
        <span className="new-project-badge" data-testid="new-project-badge">{t.newProject.badge}</span>
        <div className="new-project-content">
          <h2 className="new-project-title" data-testid="new-project-title">{t.newProject.title}</h2>
          <p className="new-project-subtitle">{t.newProject.subtitle}</p>
          <Link to={localizePath('/real-estate#project-avrupa-gunesli', language)} className="new-project-cta" data-testid="new-project-cta">
            {t.newProject.cta} <i className={`fas fa-arrow-${language === 'ar' ? 'left' : 'right'}`}></i>
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section" data-testid="about-section">
        <div className="section-bg-carousel">
          {sectionBgImages.map((img, idx) => (
            <div key={idx} className={`section-bg-slide ${idx === sectionBgIndex ? 'active' : ''}`} style={{ backgroundImage: `url(${img})` }}></div>
          ))}
          <div className="section-bg-overlay"></div>
        </div>
        <div className="container">
          <h2 className="section-title scroll-reveal">{t.about.title}</h2>
          <div className="about-content">
            <p className="about-intro">{t.about.intro}</p>
            <div className="services-grid">
              <Link to={localizePath('/travel', language)} className="service-card has-image service-card-link" data-testid="service-card-tourism">
                <div className="service-card-img"><img src="https://customer-assets.emergentagent.com/job_e1f4408b-0273-4e3a-a89a-6d5a2e02000a/artifacts/hdusd8g0_8istanbu2.jpg" alt={t.about.tourism} loading="lazy" /></div>
                <h3>{t.about.tourism}</h3><p>{t.about.tourismDesc}</p></Link>
              <Link to={localizePath('/real-estate', language)} className="service-card has-image service-card-link" data-testid="service-card-realestate">
                <div className="service-card-img"><img src="https://customer-assets.emergentagent.com/job_e1f4408b-0273-4e3a-a89a-6d5a2e02000a/artifacts/0n43146m_1750307297640.jpg" alt={t.about.realEstate} loading="lazy" /></div>
                <h3>{t.about.realEstate}</h3><p>{t.about.realEstateDesc}</p><p className="additional-info">{t.about.realEstateExtra}</p></Link>
              <div className="service-card has-image" data-testid="service-card-residence">
                <div className="service-card-img"><img src="/images/service-residence-permit-turkey.webp" alt={t.about.residence} /></div>
                <h3>{t.about.residence}</h3><p>{t.about.residenceDesc}</p>
              </div>
              <div className="service-card has-image" data-testid="service-card-citizenship">
                <div className="service-card-img"><img src="/images/service-turkish-citizenship-investment.webp" alt={t.about.citizenship} loading="lazy" /></div>
                <h3>{t.about.citizenship}</h3><p>{t.about.citizenshipDesc}</p></div>
              <div className="service-card has-image" data-testid="service-card-business">
                <div className="service-card-img"><img src="/images/service-establish-business-turkey.webp" alt={t.about.establishBusiness} /></div>
                <h3>{t.about.establishBusiness}</h3><p>{t.about.establishBusinessDesc}</p>
              </div>
              <div className="service-card has-image" data-testid="service-card-workpermit">
                <div className="service-card-img"><img src="/images/service-work-permit-turkey.webp" alt={t.about.workPermit} /></div>
                <h3>{t.about.workPermit}</h3><p>{t.about.workPermitDesc}</p>
              </div>
            </div>
            <p className="about-intro" data-testid="home-layover-link-text">
              {language === 'ar' ? <>هل لديك ساعات قليلة في إسطنبول بين رحلتين؟ اطّلع على <Link to={localizePath('/layover-tours-istanbul', language)} data-testid="home-layover-link">جولات الترانزيت في إسطنبول</Link> — جولات خاصة من المطار إلى المطار مع مرشد مرخّص. هل تصل على متن سفينة سياحية؟ تصفّح <Link to="/tours/galataport-shore-excursions" data-testid="home-galataport-link">جدول رحلات غلطة بورت 2026 والجولات الشاطئية</Link>.</> : <>Only in Istanbul for a few hours between flights? See our <Link to="/layover-tours-istanbul" data-testid="home-layover-link">Istanbul layover tours</Link> — private airport-to-airport tours with a licensed guide. Arriving by cruise ship? Check the <Link to="/tours/galataport-shore-excursions" data-testid="home-galataport-link">2026 Galataport cruise schedule and shore excursions</Link>.</>}
            </p>

            {/* Founder Profile */}
            <div className="founder-profile scroll-reveal" data-testid="founder-profile">
              <div className="founder-photo">
                <img src="/images/akin-guzel-founder.webp" alt={t.about.founderName} />
              </div>
              <div className="founder-info">
                <p className="founder-greeting">{t.about.founderGreeting}</p>
                <p className="founder-name">{t.about.founderName}</p>
                <p className="founder-title-text">{t.about.founderTitle}</p>
              </div>
            </div>

            {/* Interest Field Cards */}
            <div className="interest-field-section" data-testid="interest-field-section">
              <h3 className="interest-field-title scroll-reveal">{t.interestField.title}</h3>
              <div className="interest-field-grid">
                <Link to={localizePath('/travel', language)} className="interest-field-card" data-testid="interest-travel-link">
                  <div className="interest-field-img">
                    <img src="https://images.unsplash.com/photo-1763965367129-b2f236547429?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600" alt={language === 'ar' ? 'سفر فياتركي' : 'Travel Viaturkey'} loading="lazy" />
                    <div className="interest-field-overlay"></div>
                  </div>
                  <div className="interest-field-body">
                    <h4>{t.interestField.travel}</h4>
                    <p>{t.interestField.travelDesc}</p>
                  </div>
                </Link>
                <Link to={localizePath('/real-estate', language)} className="interest-field-card" data-testid="interest-invest-link">
                  <div className="interest-field-img">
                    <img src="https://images.unsplash.com/photo-1753356965926-6e22d3467fee?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600" alt={language === 'ar' ? 'استثمار فياتركي' : 'Invest Viaturkey'} loading="lazy" />
                    <div className="interest-field-overlay"></div>
                  </div>
                  <div className="interest-field-body">
                    <h4>{t.interestField.invest}</h4>
                    <p>{t.interestField.investDesc}</p>
                  </div>
                </Link>
                <a href="#investment-insights" className="interest-field-card" data-testid="interest-insights-link">
                  <div className="interest-field-img">
                    <img src="https://images.unsplash.com/photo-1581355319238-3e9e82e684e0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600" alt={language === 'ar' ? 'رؤى الاستثمار من فياتركي' : 'Invest Insights Viaturkey'} loading="lazy" />
                    <div className="interest-field-overlay"></div>
                  </div>
                  <div className="interest-field-body">
                    <h4>{t.interestField.insights}</h4>
                    <p>{t.interestField.insightsDesc}</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Showcase */}
      <ReviewsShowcase />

      {/* Investment Insights Section */}
      <section id="investment-insights" className="news-section" data-testid="investment-insights-section">
        <div className="container">
          <h2 className="section-title scroll-reveal">{t.investmentInsights.title}</h2>

          {/* Kanal Istanbul Project Cards */}
          <h3 className="kanal-istanbul-title" data-testid="kanal-istanbul-title">{t.kanalIstanbul.sectionTitle}</h3>
          <div className="kanal-istanbul-grid" data-testid="kanal-istanbul-grid">
            <a href="https://www.kanalistanbul.gov.tr/tr/video" target="_blank" rel="noopener noreferrer" className="kanal-istanbul-card" data-testid="kanal-project-purpose-link">
              <div className="kanal-istanbul-card-img">
                <img src="https://customer-assets.emergentagent.com/job_08630688-08aa-4216-a68f-909227f2d501/artifacts/riihytra_e1fff92ee3d92020ac62c6ba621ca506.jpg" alt={language === 'ar' ? 'خريطة مشروع قناة إسطنبول' : 'Kanal Istanbul Project Map'} loading="lazy" />
              </div>
              <div className="kanal-istanbul-card-body">
                <span className="kanal-istanbul-badge">kanalistanbul.gov.tr</span>
                <h4>{t.kanalIstanbul.projectPurpose}</h4>
                <p>{t.kanalIstanbul.projectPurposeDesc}</p>
              </div>
            </a>
            <a href="https://www.kanalistanbul.gov.tr/tr/video" target="_blank" rel="noopener noreferrer" className="kanal-istanbul-card" data-testid="kanal-videos-link">
              <div className="kanal-istanbul-card-img">
                <img src="https://customer-assets.emergentagent.com/job_08630688-08aa-4216-a68f-909227f2d501/artifacts/5hn0vlpt_kanal-istanbul.jpg" alt={language === 'ar' ? 'خريطة مسار قناة إسطنبول' : 'Kanal Istanbul Route Map'} loading="lazy" />
              </div>
              <div className="kanal-istanbul-card-body">
                <span className="kanal-istanbul-badge">kanalistanbul.gov.tr</span>
                <h4>{t.kanalIstanbul.videos}</h4>
                <p>{t.kanalIstanbul.videosDesc}</p>
              </div>
            </a>
          </div>

          {/* Kanal Istanbul Arabic Video */}
          <div className="kanal-istanbul-video-section" data-testid="kanal-arabic-video-section">
            <div className="kanal-istanbul-card" style={{maxWidth: '700px', margin: '1.5rem auto'}}>
              <div className="kanal-istanbul-card-body" style={{padding: '1rem 1.5rem 0.5rem'}}>
                <span className="kanal-istanbul-badge" style={{background: '#dcfce7', color: '#166534'}}>Viaturkey</span>
                <h4>{t.kanalIstanbul.arabicVideoTitle}</h4>
                <p>{t.kanalIstanbul.arabicVideoDesc}</p>
                <div className="kanal-video-tags">
                  <span className="kanal-tag">#عقارات_تركيا</span>
                  <span className="kanal-tag">#اسطنبول</span>
                  <span className="kanal-tag">#تركيا_بعيون_عربية</span>
                  <span className="kanal-tag">#تركيا</span>
                  <span className="kanal-tag">#عقارات_اسطنبول</span>
                  <span className="kanal-tag">#قناة_اسطنبول</span>
                </div>
              </div>
              <video controls playsInline preload="metadata" style={{width: '100%', borderRadius: '0 0 12px 12px'}} data-testid="kanal-arabic-video">
                <source src="/videos/kanal-istanbul-arabic-documentary.mp4" type="video/mp4" />
              </video>
            </div>
          </div>

          {/* Tersane Istanbul / Aliée Section */}
          <h3 className="kanal-istanbul-title" data-testid="tersane-istanbul-title">{t.tersaneIstanbul.sectionTitle}</h3>
          <p className="tersane-description">{t.tersaneIstanbul.description}</p>
          <div className="kanal-istanbul-grid" data-testid="tersane-istanbul-grid">
            <a href="https://alieeistanbul.com/en/our-story" target="_blank" rel="noopener noreferrer" className="kanal-istanbul-card" data-testid="tersane-our-story-link">
              <div className="kanal-istanbul-card-img">
                <img src="https://cms.alieeistanbul.com/images/sections/page-04112025122911.jpg" alt={language === 'ar' ? 'ترسانة إسطنبول — قصتنا' : 'Tersane Istanbul - Our Story'} loading="lazy" />
              </div>
              <div className="kanal-istanbul-card-body">
                <span className="kanal-istanbul-badge" style={{background: '#dbeafe', color: '#1e40af'}}>alieeistanbul.com</span>
                <h4>{t.tersaneIstanbul.ourStory}</h4>
                <p>{t.tersaneIstanbul.ourStoryDesc}</p>
              </div>
            </a>
            <a href="https://alieeistanbul.com/en/aliee-things-to-do" target="_blank" rel="noopener noreferrer" className="kanal-istanbul-card" data-testid="tersane-things-to-do-link">
              <div className="kanal-istanbul-card-img">
                <img src="https://cms.alieeistanbul.com/images/blog/blog-13102025164441.jpg" alt={language === 'ar' ? 'Aliée إسطنبول — أنشطة وتجارب' : 'Aliée Istanbul - Things to Do'} loading="lazy" />
              </div>
              <div className="kanal-istanbul-card-body">
                <span className="kanal-istanbul-badge" style={{background: '#dbeafe', color: '#1e40af'}}>alieeistanbul.com</span>
                <h4>{t.tersaneIstanbul.thingsToDo}</h4>
                <p>{t.tersaneIstanbul.thingsToDoDesc}</p>
              </div>
            </a>
          </div>

          {/* Market & Tourism News (curated links) */}
          <h3 className="kanal-istanbul-title" data-testid="market-news-title">{t.investmentInsights.marketNews}</h3>
          <div className="featured-news-grid" data-testid="featured-news-grid">
            <a href="https://www.dailysabah.com/business/tourism/turkiye-tops-2025-tourism-revenue-target-foreign-arrivals-hit-record" target="_blank" rel="noopener noreferrer" className="kanal-istanbul-card" data-testid="news-tourism-revenue">
              <div className="kanal-istanbul-card-img">
                <img src="https://idsb.tmgrup.com.tr/ly/uploads/images/2026/01/30/thumbs/1200x675/424354.jpg" alt={language === 'ar' ? 'رقم قياسي في إيرادات السياحة' : 'Tourism Revenue Record'} loading="lazy" />
              </div>
              <div className="kanal-istanbul-card-body">
                <span className="kanal-istanbul-badge" style={{background: '#dcfce7', color: '#166534'}}>dailysabah.com</span>
                <h4>{t.news.tourismRevenue.title}</h4>
                <p>{t.news.tourismRevenue.desc}</p>
              </div>
            </a>
            <a href="https://www.invest.gov.tr/en/sectors/pages/real-estate.aspx" target="_blank" rel="noopener noreferrer" className="kanal-istanbul-card" data-testid="news-real-estate-market">
              <div className="kanal-istanbul-card-img">
                <img src="https://customer-assets.emergentagent.com/job_08630688-08aa-4216-a68f-909227f2d501/artifacts/rh295nf5_invtur_logo_2362x1233.png" alt={language === 'ar' ? 'استثمر في تركيا' : 'Invest in Turkiye'} loading="lazy" style={{objectFit: 'contain', background: 'white', padding: '1rem'}} />
              </div>
              <div className="kanal-istanbul-card-body">
                <span className="kanal-istanbul-badge" style={{background: '#fef3c7', color: '#92400e'}}>invest.gov.tr</span>
                <h4>{t.news.realEstateMarket.title}</h4>
                <p>{t.news.realEstateMarket.desc}</p>
                <span className="featured-news-cta">{t.news.realEstateMarket.cta} &rarr;</span>
              </div>
            </a>
            <a href="https://www.aa.com.tr/en/turkiye/turkiye-posts-record-high-tourism-revenue-and-tourist-count-despite-geopolitical-challenges/3825285" target="_blank" rel="noopener noreferrer" className="kanal-istanbul-card" data-testid="news-tourism-record">
              <div className="kanal-istanbul-card-img">
                <img src="https://cdnuploads.aa.com.tr/uploads/Contents/2026/02/09/thumbs_b_c_1655ad8725040404505ea1103bb3cd2b.jpg" alt={language === 'ar' ? 'رقم قياسي سياحي' : 'Tourism Record'} loading="lazy" />
              </div>
              <div className="kanal-istanbul-card-body">
                <span className="kanal-istanbul-badge" style={{background: '#fee2e2', color: '#991b1b'}}>aa.com.tr</span>
                <h4>{t.news.tourismRecord.title}</h4>
                <p>{t.news.tourismRecord.desc}</p>
              </div>
            </a>
          </div>


          <DiningCards t={t} language={language} testIdPrefix="home-dining" sectionTestId="home-business-dining-section" />
        </div>
      </section>
      <section className="goturkiye-blog-section" data-testid="goturkiye-blog-section">
        <div className="container">
          <div className="goturkiye-blog-header">
            <img src="https://cdn.goturkiye.com/branding/goturkiye-white.svg" alt="GoTurkiye" className="goturkiye-logo" data-testid="goturkiye-logo" />
            <p className="goturkiye-blog-intro">{t.goturkiyeBlog.intro}</p>
          </div>
          <div className="goturkiye-blog-grid">
            {goturkiyeBlogLinks.map((blog, idx) => (
              <a key={idx} href={blog.link} target="_blank" rel="noopener noreferrer" className={`goturkiye-blog-card ${blog.isNomad ? 'nomad-highlight' : ''}`} data-testid={`goturkiye-blog-card-${idx}`}>
                <div className="goturkiye-blog-card-img"><img src={blog.image} alt={t.goturkiyeBlog.blogTitles[idx]} loading="lazy" /></div>
                <div className="goturkiye-blog-card-title">{t.goturkiyeBlog.blogTitles[idx]}</div>
                {blog.isNomad && <div className="nomad-cta-text">{t.digitalNomad?.cta || 'For the most reliable and up-to-date information, please visit the link'}</div>}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Tags Section */}
      {/* Callback Request Section */}
      <section className="callback-section" data-testid="callback-section">
        <div className="container">
          <CallbackForm language={language} />
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section" data-testid="newsletter-section">
        <div className="container">
          <NewsletterSignup language={language} />
        </div>
      </section>

      {/* Google Maps Location */}
      <section className="map-section" data-testid="map-section">
        <div className="container">
          <h2 className="section-title">{t.map.title}</h2>
          <div className="map-container">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3010.2!2d28.925447!3d41.006880!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab9be8e4b8f07%3A0x1b1b1b1b1b1b1b1b!2sFatih%2C%20Istanbul!5e0!3m2!1sen!2str!4v1716940800000!5m2!1sen!2str" width="100%" height="450" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Viaturkey Office Location"></iframe>
            <div className="map-info">
              <p><strong>Viaturkey Investments & Tourism Co.</strong></p>
              <p>&#128205; {language === 'ar' ? 'العنوان: ' : ''}Yedi Emirler Sk. No:18/9</p>
              <p>Fatih/Istanbul/Turkey</p>
              <a href="https://maps.app.goo.gl/MwZV3xgqbxCq9Ja98" target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{marginTop: '1rem'}}>{t.map.openInMaps}</a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section" data-testid="contact-section">
        <div className="section-bg-carousel">
          {sectionBgImages.map((img, idx) => (
            <div key={idx} className={`section-bg-slide ${idx === ((sectionBgIndex + 3) % 5) ? 'active' : ''}`} style={{ backgroundImage: `url(${img})` }}></div>
          ))}
          <div className="section-bg-overlay"></div>
        </div>
        <div className="container">
          <h2 className="section-title">{t.contact.title}</h2>
          <div className="contact-grid">
            <div className="contact-info">
              <h3>{t.contact.getInTouch}</h3>
              <p>&#128231; <a href="mailto:akin@viaturkey.net">akin@viaturkey.net</a></p>
              <p>&#128241; <a href="https://wa.me/905075337545" target="_blank" rel="noopener noreferrer">WhatsApp: +90 507 533 7545</a></p>
              <p>&#128205; Yedi Emirler Sk. No:18/9<br/>Fatih/Istanbul/Turkey</p>
              <div className="social-links-section">
                <h4 style={{color: '#1e3a8a', marginTop: '2rem', marginBottom: '1rem'}}>{language === 'ar' ? 'تابعونا' : language === 'ru' ? 'Подписывайтесь на нас' : 'Follow Us'}</h4>
                <a href="https://www.youtube.com/@akinguzel1490" target="_blank" rel="noopener noreferrer" className="social-link-card" data-testid="youtube-link">
                  <svg className="social-logo" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  <span>{t.contact.followYoutube}</span>
                </a>
                <a href="https://www.instagram.com/via_turkey" target="_blank" rel="noopener noreferrer" className="social-link-card" data-testid="instagram-link">
                  <svg className="social-logo" viewBox="0 0 24 24" fill="url(#instagram-gradient)"><defs><linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" style={{stopColor: '#FED373', stopOpacity: 1}} /><stop offset="15%" style={{stopColor: '#F15245', stopOpacity: 1}} /><stop offset="35%" style={{stopColor: '#D92E7F', stopOpacity: 1}} /><stop offset="65%" style={{stopColor: '#9B36B7', stopOpacity: 1}} /><stop offset="100%" style={{stopColor: '#515ECF', stopOpacity: 1}} /></linearGradient></defs><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  <span>{t.contact.followInstagram}</span>
                </a>
                <a href="https://www.tiktok.com/@akingzel" target="_blank" rel="noopener noreferrer" className="social-link-card" data-testid="tiktok-link">
                  <svg className="social-logo" viewBox="0 0 24 24" fill="#000000"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  <span>{t.contact.followTiktok}</span>
                </a>
                <a href="https://x.com/akngzl3" target="_blank" rel="noopener noreferrer" className="social-link-card" data-testid="twitter-link">
                  <svg className="social-logo" viewBox="0 0 24 24" fill="#000000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  <span>{t.contact.followTwitter}</span>
                </a>
              </div>
            </div>
            <div className="contact-form-container">
              <h3>{t.contact.sendMessage}</h3>
              <a href="https://calendly.com/guzaliakin234" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="calendly-booking-btn">{t.contact.openForm}</a>
            </div>
          </div>
        </div>
      </section>

      <FAQSection t={t} restaurants={restaurants} language={language} />
      <StatsSection t={t} />
      <ExchangeRatesWidget language={language} exchangeRates={exchangeRates} exchangeRatesLoading={exchangeRatesLoading} exchangeRatesError={exchangeRatesError} fetchRates={fetchRates} />
      <WeatherWidget language={language} weather={weather} weatherLoading={weatherLoading} weatherError={weatherError} fetchWeather={fetchWeather} />
      <Footer t={t} />

      {/* Quality Stickers */}

      {/* Inquiry Modal - Calendly Redirect */}
      {showInquiryModal && (
        <div className="modal-overlay" onClick={() => setShowInquiryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="inquiry-modal">
            <button className="modal-close" onClick={() => setShowInquiryModal(false)}>&times;</button>
            <h2>{t.inquiry.title}</h2>
            <p style={{ color: '#475569', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              {t.realEstate?.discoveryCall || 'Book a 15-minute discovery call to discuss your investment goals in Istanbul & Turkiye.'}
            </p>
            <a href="https://calendly.com/guzaliakin234" target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-block', textAlign: 'center', width: '100%' }} data-testid="calendly-link">
              {t.inquiry.bookCall || 'Book an Appointment'}
            </a>
          </div>
        </div>
      )}

      {message.text && (<div className={`toast toast-${message.type}`} data-testid="toast-message">{message.text}</div>)}

      {/* Floating Calendly CTA - Home page bottom left */}
      <a href="https://calendly.com/guzaliakin234" target="_blank" rel="noopener noreferrer" className="floating-calendly-cta floating-calendly-left" data-testid="floating-calendly-cta">
        <span className="calendly-pulse"></span>
        <span className="calendly-text">{language === 'ru' ? 'Бесплатная консультация 15 мин' : language === 'ar' ? 'استشارة مجانية 15 دقيقة' : language === 'tr' ? '15 dk Ücretsiz Danışmanlık' : '15-Min Free Consultation'}</span>
      </a>
    </div>
  );
}

function App() {
  const [language, setLanguage] = useState(() => {
    if (isArPath(window.location.pathname)) return 'ar';
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    if (['en', 'ru', 'ar', 'tr'].includes(lang)) return lang;
    return 'en';
  });
  const [cookieConsent, setCookieConsent] = useState(() => {
    try { return sessionStorage.getItem('viaturkey_cookie_consent') === 'accepted'; } catch { return false; }
  });

  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const trackEvent = useCallback((eventType, page, element) => {
    if (!cookieConsent) return;
    const sessionId = sessionStorage.getItem('viaturkey_session') || (() => {
      const id = Math.random().toString(36).substring(2);
      sessionStorage.setItem('viaturkey_session', id);
      return id;
    })();
    axios.post(`${API}/analytics/track`, {
      event_type: eventType, page, element, session_id: sessionId, language
    }).catch(() => {});
  }, [cookieConsent, language]);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <StickyNav language={language} setLanguage={setLanguage} t={translations[language]} />
      <LanguageSync language={language} setLanguage={setLanguage} />
      <AnalyticsTracker trackEvent={trackEvent} cookieConsent={cookieConsent} />
      <Routes>
        <Route path="/" element={<HomePage language={language} setLanguage={setLanguage} />} />
        <Route path="/travel" element={<TravelPage language={language} setLanguage={setLanguage} />} />
        <Route path="/tours" element={<TravelPage language={language} setLanguage={setLanguage} />} />
        <Route path="/tours/galataport-shore-excursions" element={<GalataportShoreExcursionsPage language={language} />} />
        <Route path="/tours/:slug" element={<ServicePage language={language} setLanguage={setLanguage} />} />
        <Route path="/real-estate" element={<RealEstatePage language={language} setLanguage={setLanguage} />} />
        <Route path="/real-estate/tips-buying-real-estate-istanbul" element={<TipsBuyingRealEstateIstanbulPage language={language} />} />
        <Route path="/blog" element={<BlogPage language={language} setLanguage={setLanguage} />} />
        <Route path="/blog/where-to-eat-near-galataport-mumhane-caddesi" element={<BlogPostMumhanePage language={language} />} />
        <Route path="/blog/category/:category" element={<BlogCategoryPage language={language} setLanguage={setLanguage} />} />
        <Route path="/blog/:slug" element={<BlogPostPage language={language} setLanguage={setLanguage} />} />
        <Route path="/faq" element={<FAQPage language={language} setLanguage={setLanguage} />} />
        <Route path="/istanbul/fatih-old-city" element={<FatihOldCityPage language={language} setLanguage={setLanguage} t={translations[language]} />} />
        <Route path="/istanbul/:slug" element={<DestinationPage language={language} setLanguage={setLanguage} />} />
        <Route path="/reviews" element={<ReviewsPage language={language} setLanguage={setLanguage} />} />
        <Route path="/contact" element={<ContactPage language={language} setLanguage={setLanguage} />} />
        <Route path="/private-istanbul-tours" element={<PrivateIstanbulToursPage language={language} />} />
        <Route path="/istanbul-old-city-tour" element={<IstanbulOldCityTourPage language={language} />} />
        <Route path="/tours/private-istanbul-tour" element={<CanonicalRedirect canonicalUrl="https://viaturkey.net/private-istanbul-tours" redirectTo="/private-istanbul-tours" />} />
        <Route path="/istanbul-tours-faq" element={<IstanbulToursFAQPage language={language} />} />
        <Route path="/about/akin-guzel" element={<AuthorProfilePage language={language} />} />
        <Route path="/layover-tours-istanbul" element={<LayoverToursPage language={language} />} />
        <Route path="/layover-tours-istanbul/:slug" element={<LayoverChildPage language={language} />} />
        <Route path="/services" element={<ServicesPage language={language} />} />
        <Route path="/admin" element={<AdminPage />} />
        {/* Arabic routes — only pages with genuine Arabic content */}
        <Route path="/ar" element={<HomePage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/travel" element={<TravelPage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/tours/:slug" element={<ServicePage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/real-estate" element={<RealEstatePage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/blog" element={<BlogPage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/blog/:slug" element={<BlogPostPage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/faq" element={<FAQPage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/reviews" element={<ReviewsPage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/contact" element={<ContactPage language="ar" setLanguage={setLanguage} />} />
        <Route path="/ar/layover-tours-istanbul" element={<LayoverToursPage language="ar" />} />
        <Route path="/ar/layover-tours-istanbul/:slug" element={<LayoverChildPage language="ar" />} />
      </Routes>
      <ChatBot language={language} />
      {/* Global WhatsApp Float with Live Status */}
      <WhatsAppFloat language={language} />
      <CookieConsent
        language={language}
        onAccept={() => setCookieConsent(true)}
        onDecline={() => setCookieConsent(false)}
      />
      {/* Floating Language Switcher removed — StickyNav handles language switching */}
    </BrowserRouter>
    </HelmetProvider>
  );
}

function AnalyticsTracker({ trackEvent, cookieConsent }) {
  const location = useLocation();
  useEffect(() => {
    if (cookieConsent) {
      trackEvent('page_view', location.pathname);
    }
  }, [location.pathname, cookieConsent, trackEvent]);

  useEffect(() => {
    if (!cookieConsent) return;
    const handleClick = (e) => {
      const target = e.target.closest('[data-testid]');
      if (target) {
        trackEvent('click', location.pathname, target.getAttribute('data-testid'));
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [cookieConsent, trackEvent, location.pathname]);

  return null;
}

function WhatsAppFloat({ language }) {
  const [isOnline, setIsOnline] = useState(() => istanbulOpenNow());

  useEffect(() => {
    const interval = setInterval(() => setIsOnline(istanbulOpenNow()), 60000);
    return () => clearInterval(interval);
  }, []);

  const onlineMsg = {
    en: 'Hello, I found your website and I\'m interested in your Istanbul tours.',
    ar: '\u0645\u0631\u062d\u0628\u0627\u064b\u060c \u0648\u062c\u062f\u062a \u0645\u0648\u0642\u0639\u0643\u0645 \u0648\u0623\u0646\u0627 \u0645\u0647\u062a\u0645 \u0628\u062c\u0648\u0644\u0627\u062a\u0643\u0645 \u0641\u064a \u0625\u0633\u0637\u0646\u0628\u0648\u0644.',
    ru: '\u0417\u0434\u0440\u0430\u0432\u0441\u0442\u0432\u0443\u0439\u0442\u0435, \u044f \u043d\u0430\u0448\u0451\u043b \u0432\u0430\u0448 \u0441\u0430\u0439\u0442 \u0438 \u0437\u0430\u0438\u043d\u0442\u0435\u0440\u0435\u0441\u043e\u0432\u0430\u043d \u0432 \u0432\u0430\u0448\u0438\u0445 \u0442\u0443\u0440\u0430\u0445 \u043f\u043e \u0421\u0442\u0430\u043c\u0431\u0443\u043b\u0443.',
    tr: 'Merhaba, web sitenizi buldum ve Istanbul turlarinizla ilgileniyorum.',
  };
  const offlineMsg = {
    en: 'Hi, I know you\'re offline but I\'d like to learn more about your Istanbul tours. Please contact me when available.',
    ar: '\u0645\u0631\u062d\u0628\u0627\u064b\u060c \u0623\u0639\u0644\u0645 \u0623\u0646\u0643\u0645 \u063a\u064a\u0631 \u0645\u062a\u0635\u0644\u064a\u0646 \u0644\u0643\u0646\u0646\u064a \u0623\u0631\u063a\u0628 \u0641\u064a \u0645\u0639\u0631\u0641\u0629 \u0627\u0644\u0645\u0632\u064a\u062f \u0639\u0646 \u062c\u0648\u0644\u0627\u062a\u0643\u0645 \u0641\u064a \u0625\u0633\u0637\u0646\u0628\u0648\u0644. \u064a\u0631\u062c\u0649 \u0627\u0644\u062a\u0648\u0627\u0635\u0644 \u0645\u0639\u064a \u0639\u0646\u062f\u0645\u0627 \u062a\u0643\u0648\u0646\u0648\u0646 \u0645\u062a\u0627\u062d\u064a\u0646.',
    ru: '\u0417\u0434\u0440\u0430\u0432\u0441\u0442\u0432\u0443\u0439\u0442\u0435, \u044f \u0437\u043d\u0430\u044e \u0447\u0442\u043e \u0432\u044b \u043e\u0444\u043b\u0430\u0439\u043d, \u043d\u043e \u0445\u043e\u0442\u0435\u043b \u0431\u044b \u0443\u0437\u043d\u0430\u0442\u044c \u0431\u043e\u043b\u044c\u0448\u0435 \u043e \u0432\u0430\u0448\u0438\u0445 \u0442\u0443\u0440\u0430\u0445. \u0421\u0432\u044f\u0436\u0438\u0442\u0435\u0441\u044c \u0441\u043e \u043c\u043d\u043e\u0439 \u043a\u043e\u0433\u0434\u0430 \u0431\u0443\u0434\u0435\u0442\u0435 \u043d\u0430 \u0441\u0432\u044f\u0437\u0438.',
    tr: 'Merhaba, cevrimdisi oldugunuzu biliyorum ama Istanbul turlariniz hakkinda bilgi almak istiyorum. Musait oldugunuzda benimle iletisime gecin.',
  };
  const statusLabels = CHAT_LABELS[language] || CHAT_LABELS.en;
  const msgText = isOnline ? (onlineMsg[language] || onlineMsg.en) : (offlineMsg[language] || offlineMsg.en);
  const label = isOnline ? statusLabels.statusOpen : statusLabels.statusClosed;

  return (
    <a href={`https://wa.me/905075337545?text=${encodeURIComponent(msgText)}`} target="_blank" rel="noopener noreferrer"
      className={`global-wa-float ${isOnline ? 'wa-online' : 'wa-offline'}`} data-testid="global-whatsapp-float" aria-label="WhatsApp">
      <svg viewBox="0 0 32 32" fill="white" className="global-wa-icon"><path d="M16 0C7.164 0 0 7.164 0 16c0 2.828 0.738 5.484 2.024 7.792L0.156 31.844l8.384-2.196C10.78 30.856 13.324 31.6 16 31.6c8.836 0 16-7.164 16-16S24.836 0 16 0zm0 29.2c-2.444 0-4.748-0.668-6.712-1.832l-0.48-0.284-4.972 1.304 1.328-4.856-0.312-0.496C3.34 20.996 2.6 18.564 2.6 16 2.6 8.608 8.608 2.6 16 2.6S29.4 8.608 29.4 16 23.392 29.2 16 29.2zm7.788-10.636c-0.428-0.212-2.524-1.248-2.916-1.388-0.392-0.144-0.676-0.212-0.96 0.212-0.284 0.428-1.1 1.388-1.348 1.672-0.248 0.284-0.496 0.32-0.924 0.108-0.428-0.212-1.804-0.664-3.436-2.12-1.268-1.132-2.124-2.528-2.372-2.956-0.248-0.428-0.028-0.66 0.184-0.872 0.192-0.188 0.428-0.496 0.64-0.744 0.212-0.248 0.284-0.428 0.428-0.712 0.144-0.284 0.072-0.532-0.036-0.744-0.108-0.212-0.96-2.312-1.316-3.164-0.348-0.828-0.7-0.716-0.96-0.728-0.248-0.012-0.532-0.016-0.816-0.016s-0.744 0.108-1.132 0.532c-0.392 0.428-1.496 1.46-1.496 3.56s1.532 4.132 1.744 4.416c0.212 0.284 3 4.58 7.26 6.42 1.016 0.44 1.808 0.704 2.428 0.9 1.02 0.324 1.948 0.28 2.684 0.168 0.82-0.12 2.524-1.032 2.88-2.028 0.356-0.996 0.356-1.852 0.248-2.028-0.104-0.18-0.388-0.288-0.816-0.5z"/></svg>
      <span className="wa-status-badge" data-testid="wa-status-badge">
        <span className={`wa-dot ${isOnline ? 'wa-dot-green' : 'wa-dot-amber'}`}></span>
        <span className="wa-status-text">{label}</span>
      </span>
    </a>
  );
}

function CanonicalRedirect({ canonicalUrl, redirectTo }) {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => navigate(redirectTo, { replace: true }), 1500);
    return () => clearTimeout(timer);
  }, [navigate, redirectTo]);

  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="noindex, follow" />
      <meta http-equiv="refresh" content={`0;url=${redirectTo}`} />
      <title>Redirecting to Private Istanbul Tours | Viaturkey</title>
    </Helmet>
  );
}

export default App;
